import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import type {
  ConferenceDashboardClient,
  OtherRevenueCategory,
  OtherRevenueRecord,
} from "./portfolio-overview-api";
import { portfolioErrorMessageKey } from "./portfolio-api-errors";
import { RevenueField } from "./RevenueField";
import { RiyalAmount, RiyalSymbol } from "./RiyalAmount";
import {
  FINANCIAL_REFERENCE_PATTERN,
  localDateTimeInputValue,
} from "./revenue-form-state";
import { useRevenueDraft, type RevenueDraftReporter } from "./useRevenueDraft";
import {
  bidiIsolationAttributes,
  useLocale,
  type MessageKey,
} from "./localization";

const CATEGORIES: readonly OtherRevenueCategory[] = [
  "exhibition",
  "workshop",
  "grant",
  "donation",
  "service",
  "other",
];

const ignoreDraft: RevenueDraftReporter = () => undefined;

const CATEGORY_MESSAGE_KEYS = {
  exhibition: "otherRevenue.category.exhibition",
  workshop: "otherRevenue.category.workshop",
  grant: "otherRevenue.category.grant",
  donation: "otherRevenue.category.donation",
  service: "otherRevenue.category.service",
  other: "otherRevenue.category.other",
} as const satisfies Readonly<Record<OtherRevenueCategory, MessageKey>>;

const METHOD_MESSAGE_KEYS = {
  bank_transfer: "finance.method.bankTransfer",
  bank_deposit: "finance.method.bankDeposit",
  other: "finance.method.other",
} as const satisfies Readonly<Record<OtherRevenueRecord["method"], MessageKey>>;

const STATUS_MESSAGE_KEYS = {
  active: "otherRevenue.status.active",
  reversed: "otherRevenue.status.reversed",
} as const satisfies Readonly<Record<OtherRevenueRecord["status"], MessageKey>>;

function parseMinorUnits(value: string): number | null {
  const match = value.trim().match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match?.[1]) return null;
  const amount =
    Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}

function OtherRevenueForm({
  conferenceId,
  client,
  onCreated,
  onDraftChange,
  record,
  onCancel,
}: {
  readonly conferenceId: string;
  readonly client: ConferenceDashboardClient;
  readonly onCreated: (record: OtherRevenueRecord) => void;
  readonly onDraftChange: RevenueDraftReporter;
  readonly record?: OtherRevenueRecord;
  readonly onCancel?: () => void;
}) {
  const { t } = useLocale();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [sourceOrganization, setSourceOrganization] = useState(
    record?.sourceOrganization ?? "",
  );
  const [category, setCategory] = useState<OtherRevenueCategory>(
    record?.category ?? "exhibition",
  );
  const [description, setDescription] = useState(record?.description ?? "");
  const [amount, setAmount] = useState(
    record ? (record.amountMinor / 100).toFixed(2) : "",
  );
  const [method, setMethod] = useState<
    "bank_transfer" | "bank_deposit" | "other"
  >(record?.method ?? "bank_transfer");
  const [reference, setReference] = useState(record?.reference ?? "");
  const [evidenceAttachmentId, setEvidenceAttachmentId] = useState(
    record?.evidenceAttachmentId ?? "",
  );
  const [initialTime, setInitialTime] = useState(() =>
    localDateTimeInputValue(record ? new Date(record.receivedAt) : undefined),
  );
  const [receivedAt, setReceivedAt] = useState(initialTime);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);
  useEffect(() => {
    if (record)
      detailsRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [record]);
  useRevenueDraft(
    `other-revenue-${conferenceId}`,
    busy ||
      Boolean(
        sourceOrganization ||
        description ||
        amount ||
        reference ||
        evidenceAttachmentId ||
        receivedAt !== initialTime ||
        category !== "exhibition" ||
        method !== "bank_transfer",
      ),
    onDraftChange,
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinor = parseMinorUnits(amount);
    const receivedTime = Date.parse(receivedAt);
    if (!amountMinor || !Number.isFinite(receivedTime)) {
      setMessage("otherRevenue.form.invalid");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const fields = {
        sourceOrganization,
        category,
        description,
        amountMinor,
        method,
        reference,
        evidenceAttachmentId,
        receivedAt:
          record && receivedAt === initialTime
            ? record.receivedAt
            : new Date(receivedTime).toISOString(),
      };
      const saved = record
        ? await client.updateOtherRevenue(record.otherRevenueId, {
            ...fields,
            expectedRevision: record.revision,
          })
        : await client.createOtherRevenue({
            ...fields,
            conferenceId,
            currency: "SAR",
          });
      onCreated(saved);
      if (record) return;
      setSourceOrganization("");
      setDescription("");
      setAmount("");
      setReference("");
      setEvidenceAttachmentId("");
      setCategory("exhibition");
      setMethod("bank_transfer");
      const nextTime = localDateTimeInputValue();
      setInitialTime(nextTime);
      setReceivedAt(nextTime);
      setMessage("otherRevenue.form.recorded");
    } catch (error: unknown) {
      setMessage(
        portfolioErrorMessageKey(
          error,
          record ? "otherRevenue.edit.failed" : "otherRevenue.form.failed",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details
      className="revenue-create"
      ref={detailsRef}
      open={record ? true : undefined}
    >
      <summary className="button button--primary">
        {t(record ? "otherRevenue.edit.heading" : "otherRevenue.form.add")}
      </summary>
      <form
        className="revenue-form"
        onSubmit={submit}
        autoComplete="off"
        aria-busy={busy}
      >
        <div className="revenue-form__heading">
          <h3>{t("otherRevenue.form.detailsHeading")}</h3>
          <p>{t("otherRevenue.form.requiredHint")}</p>
        </div>
        <RevenueField
          id="other-revenue-source"
          label={t("otherRevenue.form.source")}
        >
          <input
            id="other-revenue-source"
            disabled={busy}
            name="sourceOrganization"
            required
            minLength={2}
            maxLength={200}
            value={sourceOrganization}
            onChange={(event) =>
              setSourceOrganization(event.currentTarget.value)
            }
          />
        </RevenueField>
        <RevenueField
          id="other-revenue-category"
          label={t("otherRevenue.form.category")}
        >
          <select
            id="other-revenue-category"
            disabled={busy}
            name="category"
            value={category}
            onChange={(event) =>
              setCategory(event.currentTarget.value as OtherRevenueCategory)
            }
          >
            {CATEGORIES.map((option) => (
              <option key={option} value={option}>
                {t(CATEGORY_MESSAGE_KEYS[option])}
              </option>
            ))}
          </select>
        </RevenueField>
        <RevenueField
          id="other-revenue-description"
          label={`${t("otherRevenue.form.description")} *`}
          fullWidth
        >
          <textarea
            id="other-revenue-description"
            disabled={busy}
            name="description"
            required
            minLength={2}
            maxLength={1_000}
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="other-revenue-amount"
          label={
            <>
              {t("otherRevenue.form.amountLabel")} <RiyalSymbol /> *
            </>
          }
        >
          <input
            id="other-revenue-amount"
            disabled={busy}
            name="amount"
            inputMode="decimal"
            required
            placeholder={t("finance.placeholder.amount")}
            {...bidiIsolationAttributes()}
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="other-revenue-method"
          label={t("otherRevenue.form.collectionMethod")}
        >
          <select
            id="other-revenue-method"
            disabled={busy}
            name="method"
            value={method}
            onChange={(event) =>
              setMethod(
                event.currentTarget.value as
                  "bank_transfer" | "bank_deposit" | "other",
              )
            }
          >
            <option value="bank_transfer">
              {t("finance.method.bankTransfer")}
            </option>
            <option value="bank_deposit">
              {t("finance.method.bankDeposit")}
            </option>
            <option value="other">{t("finance.method.other")}</option>
          </select>
        </RevenueField>
        <h3 className="revenue-form__section">
          {t("finance.form.referencesHeading")}
        </h3>
        <RevenueField
          id="other-revenue-reference"
          label={`${t("finance.field.reference")} (${t("otherRevenue.optional")})`}
          hint={t("finance.field.referenceHint")}
        >
          <input
            id="other-revenue-reference"
            disabled={busy}
            name="reference"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby="other-revenue-reference-hint"
            spellCheck={false}
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={reference}
            onChange={(event) => setReference(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="other-revenue-evidence"
          label={`${t("finance.field.evidence")} (${t("otherRevenue.optional")})`}
          hint={t("finance.field.evidenceHint")}
        >
          <input
            id="other-revenue-evidence"
            disabled={busy}
            name="evidenceAttachmentId"
            pattern={FINANCIAL_REFERENCE_PATTERN}
            title={t("finance.field.referenceInvalid")}
            aria-describedby="other-revenue-evidence-hint"
            spellCheck={false}
            minLength={2}
            maxLength={160}
            {...bidiIsolationAttributes()}
            value={evidenceAttachmentId}
            onChange={(event) =>
              setEvidenceAttachmentId(event.currentTarget.value)
            }
          />
        </RevenueField>
        <RevenueField
          id="other-revenue-received"
          label={t("otherRevenue.form.collectedAt")}
          hint={t("finance.field.localTimeHint")}
        >
          <input
            id="other-revenue-received"
            disabled={busy}
            name="receivedAt"
            aria-describedby="other-revenue-received-hint"
            type="datetime-local"
            {...bidiIsolationAttributes()}
            required
            value={receivedAt}
            onChange={(event) => setReceivedAt(event.currentTarget.value)}
          />
        </RevenueField>
        {message ? (
          <p className="form-message" role="status">
            {t(message)}
          </p>
        ) : null}
        <div className="revenue-form__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={busy}
          >
            {busy
              ? t("finance.action.saving")
              : t(
                  record
                    ? "otherRevenue.edit.save"
                    : "otherRevenue.form.record",
                )}
          </button>
          <button
            className="button button--quiet"
            type="button"
            disabled={busy}
            onClick={() => {
              if (record) {
                onCancel?.();
                return;
              }
              setSourceOrganization("");
              setDescription("");
              setAmount("");
              setReference("");
              setEvidenceAttachmentId("");
              setCategory("exhibition");
              setMethod("bank_transfer");
              const nextTime = localDateTimeInputValue();
              setInitialTime(nextTime);
              setReceivedAt(nextTime);
              setMessage(null);
              if (detailsRef.current) {
                detailsRef.current.open = false;
                detailsRef.current.querySelector("summary")?.focus();
              }
            }}
          >
            {t("finance.form.cancel")}
          </button>
        </div>
      </form>
    </details>
  );
}

export function OtherRevenuePanel({
  conferenceId,
  client,
  canManage,
  onDraftChange = ignoreDraft,
  compact = false,
  canEdit = canManage,
  onRecordsChange,
  onAvailabilityChange,
}: {
  readonly conferenceId: string;
  readonly client: ConferenceDashboardClient;
  readonly canManage: boolean;
  readonly onDraftChange?: RevenueDraftReporter;
  readonly compact?: boolean;
  readonly canEdit?: boolean;
  readonly onRecordsChange?: (records: readonly OtherRevenueRecord[]) => void;
  readonly onAvailabilityChange?: (
    status: "loading" | "ready" | "error",
  ) => void;
}) {
  const { t, date } = useLocale();
  const [editing, setEditing] = useState<OtherRevenueRecord | null>(null);
  const [savedMessage, setSavedMessage] = useState(false);
  const [records, setRecords] = useState<readonly OtherRevenueRecord[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<MessageKey>(
    "otherRevenue.loadFailed",
  );
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [reversalError, setReversalError] = useState<{
    readonly recordId: string;
    readonly message: MessageKey;
  } | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!conferenceId) {
        setRecords([]);
        setStatus("ready");
        return Promise.resolve();
      }
      setStatus("loading");
      return client
        .loadOtherRevenue(conferenceId, signal)
        .then((loaded) => {
          setRecords(loaded);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            setErrorMessage(
              portfolioErrorMessageKey(error, "otherRevenue.loadFailed"),
            );
            setStatus("error");
          }
        });
    },
    [client, conferenceId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (status === "ready") onRecordsChange?.(records);
  }, [onRecordsChange, records, status]);
  useEffect(() => {
    onAvailabilityChange?.(status);
  }, [onAvailabilityChange, status]);

  const activeTotal = useMemo(
    () =>
      records
        .filter(({ status: recordStatus }) => recordStatus === "active")
        .reduce((sum, record) => sum + record.amountMinor, 0),
    [records],
  );

  async function reverse(record: OtherRevenueRecord) {
    if (reversingId) return;
    setReversingId(record.otherRevenueId);
    setReversalError(null);
    try {
      const reversed = await client.reverseOtherRevenue(record.otherRevenueId);
      setRecords((current) =>
        current.map((candidate) =>
          candidate.otherRevenueId === reversed.otherRevenueId
            ? reversed
            : candidate,
        ),
      );
    } catch (error: unknown) {
      setReversalError({
        recordId: record.otherRevenueId,
        message: portfolioErrorMessageKey(error, "otherRevenue.reverse.failed"),
      });
    } finally {
      setReversingId(null);
    }
  }

  return (
    <section className="other-revenue" aria-labelledby="other-revenue-title">
      <header className="section-heading">
        <div>
          <h2 id="other-revenue-title">
            {t(
              compact
                ? "otherRevenue.conferenceHeading"
                : "otherRevenue.heading",
            )}
          </h2>
          <p>{t("otherRevenue.description")}</p>
        </div>
        <strong>
          {status === "ready" ? (
            <RiyalAmount minor={activeTotal} fractionDigits={2} />
          ) : (
            "—"
          )}
        </strong>
      </header>
      {savedMessage ? (
        <p className="form-message" role="status">
          {t("otherRevenue.edit.saved")}
        </p>
      ) : null}
      {editing && canEdit ? (
        <OtherRevenueForm
          key={`${editing.otherRevenueId}-${editing.revision}`}
          conferenceId={conferenceId}
          client={client}
          record={editing}
          onDraftChange={onDraftChange}
          onCancel={() => setEditing(null)}
          onCreated={(saved) => {
            setRecords((current) =>
              current.map((candidate) =>
                candidate.otherRevenueId === saved.otherRevenueId
                  ? saved
                  : candidate,
              ),
            );
            setEditing(null);
            setSavedMessage(true);
          }}
        />
      ) : null}
      {conferenceId && canManage && !editing ? (
        <OtherRevenueForm
          conferenceId={conferenceId}
          client={client}
          onDraftChange={onDraftChange}
          onCreated={(record) => setRecords((current) => [record, ...current])}
        />
      ) : null}

      {status === "loading" ? (
        <p className="overview-state" aria-live="polite">
          {t("otherRevenue.loading")}
        </p>
      ) : status === "error" ? (
        <div className="overview-state" role="alert">
          <p>{t(errorMessage)}</p>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void load()}
          >
            {t("finance.action.tryAgain")}
          </button>
        </div>
      ) : records.length === 0 ? (
        <p className="overview-state">{t("otherRevenue.empty")}</p>
      ) : (
        <div
          className="expense-table-wrap revenue-records-table"
          role="region"
          aria-label={t(
            compact ? "otherRevenue.conferenceHeading" : "otherRevenue.heading",
          )}
          tabIndex={0}
        >
          <table className="expense-table">
            <thead>
              <tr>
                <th scope="col">{t("otherRevenue.form.source")}</th>
                <th scope="col">{t("otherRevenue.form.category")}</th>
                <th scope="col">{t("otherRevenue.form.description")}</th>
                <th scope="col">{t("otherRevenue.form.collectedAt")}</th>
                <th scope="col">{t("otherRevenue.form.amountLabel")}</th>
                <th scope="col">{t("finance.field.method")}</th>
                <th scope="col">{t("finance.field.reference")}</th>
                <th scope="col">{t("finance.field.evidence")}</th>
                <th scope="col">{t("finance.field.status")}</th>
                {canEdit || canManage ? (
                  <th scope="col">{t("otherRevenue.actions")}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {[...records]
                .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
                .map((record) => (
                  <tr key={record.otherRevenueId}>
                    <td>
                      <bdi>{record.sourceOrganization}</bdi>
                    </td>
                    <td>{t(CATEGORY_MESSAGE_KEYS[record.category])}</td>
                    <td className="revenue-records-table__description">
                      <bdi>{record.description}</bdi>
                    </td>
                    <td>
                      <bdi>
                        {date(record.receivedAt, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </bdi>
                    </td>
                    <td>
                      <RiyalAmount
                        minor={record.amountMinor}
                        currency={record.currency}
                        fractionDigits={2}
                      />
                    </td>
                    <td>{t(METHOD_MESSAGE_KEYS[record.method])}</td>
                    <td>
                      <bdi {...bidiIsolationAttributes()}>
                        {record.reference || "—"}
                      </bdi>
                    </td>
                    <td>
                      <bdi {...bidiIsolationAttributes()}>
                        {record.evidenceAttachmentId || "—"}
                      </bdi>
                    </td>
                    <td>
                      <span className="status-badge">
                        {t(STATUS_MESSAGE_KEYS[record.status])}
                      </span>
                    </td>
                    {canEdit || canManage ? (
                      <td>
                        {record.status === "active" ? (
                          <div className="revenue-records-table__actions">
                            {canEdit ? (
                              <button
                                className="button button--secondary button--small"
                                type="button"
                                disabled={!!editing || !!reversingId}
                                onClick={() => {
                                  setSavedMessage(false);
                                  setEditing(record);
                                }}
                              >
                                {t("otherRevenue.edit.action")}
                              </button>
                            ) : null}
                            {canManage ? (
                              <details className="revenue-action reversal-action">
                                <summary>
                                  {t("otherRevenue.reverse.heading")}
                                </summary>
                                <p>{t("otherRevenue.reverse.description")}</p>
                                {reversalError?.recordId ===
                                record.otherRevenueId ? (
                                  <p className="form-message" role="alert">
                                    {t(reversalError.message)}
                                  </p>
                                ) : null}
                                <button
                                  className="button button--danger"
                                  type="button"
                                  disabled={!!reversingId || !!editing}
                                  onClick={() => void reverse(record)}
                                >
                                  {reversingId === record.otherRevenueId
                                    ? t("otherRevenue.reverse.busy")
                                    : t("otherRevenue.reverse.confirm")}
                                </button>
                              </details>
                            ) : null}
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
